import { createAsyncThunk } from '@reduxjs/toolkit';

import api from '@setup/api';
import { handleServerError } from '@setup/config/serverErrorHandler';
import { NotificationService } from '@setup/notifications/SnackbarUtilsConfigurator/SnackbarUtilsConfigurator';
import { StatusServerErrors } from '@shared/enums/status-server-errors';

import i18next from '../../setup/locales/i18n';
import { setChatCounters, setLoading } from '../Common/commonSlice';

import { addThreadsItems, setThreadCounter } from './chatSlice';

import type { AppDispatch, RootState } from '../store';
import type { ErrorType, ServerErrorType } from '@shared/interfaces/error.interfaces';
import type { MessageModel, ThreadModel, UserEmoji } from '@shared/models/chat/chat.model';
import type { ResponseMetaType } from '@shared/models/general';
import type { ItemUserModel } from '@shared/models/itemUser.model';
import type { MediaType } from '@shared/models/media.model';
import type { MetaModel } from '@shared/models/meta.model';

type GetThreadsResponse = {
  data: ThreadModel[];
  meta: ResponseMetaType;
  links?: { next: string };
};

type GetMessageEmojiUsers = {
  [key: string]: UserEmoji[];
};

export const getThreads = createAsyncThunk<
  GetThreadsResponse,
  { isGroup: number; query?: string | undefined; page: number },
  { rejectValue: ErrorType; dispatch: AppDispatch; state: RootState }
>('getThreads', async ({ isGroup, query, page }, { rejectWithValue, getState }) => {
  try {
    const storeQuery = getState().chat.query;
    return await api.chat.getThreads(page, isGroup, query || storeQuery);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const getSupportThread = createAsyncThunk<
  ThreadModel,
  undefined,
  { rejectValue: ErrorType; dispatch: AppDispatch; state: RootState }
>('getSupportThread', async (_, { rejectWithValue }) => {
  try {
    return await api.chat.getSupportThread();
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
  // finally {
  //   dispatch(setThreadsFetching(false));
  // }
});

export const getThread = createAsyncThunk<
  ThreadModel,
  { id: number },
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('getThread', async ({ id }, { rejectWithValue }) => {
  try {
    return await api.chat.getThread(id);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const updateThread = createAsyncThunk<
  ThreadModel,
  { id: number },
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('updateThread', async ({ id }, { rejectWithValue }) => {
  try {
    return await api.chat.getThread(id);
  } catch (e: any) {
    const axiosError = e as ServerErrorType;

    return rejectWithValue(axiosError.data);
  }
});

export const addThread = (id: number) => async (dispatch: AppDispatch) => {
  try {
    const res = await api.chat.getThread(id);
    dispatch(addThreadsItems({ ...res, count_unread_messages: 1 }));
  } catch (e) {
    handleServerError(e);
  }
};

export const getMessages = createAsyncThunk<
  { data: MessageModel[]; direction: number; messageId?: number; messagePage?: number; isReplice: boolean },
  {
    page?: number | undefined;
    threadId: number;
    direction: number;
    id?: number;
    isReplice?: boolean;
  },
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('getMessages', async ({ page, threadId, direction, id, isReplice }, { rejectWithValue }) => {
  try {
    const res = await api.chat.getMessages(threadId, direction, page || undefined, id);
    return {
      ...res,
      data: res.data,
      direction,
      messagePage: page || undefined,
      messageId: id,
      isReplice: !!isReplice,
    };
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const sendMessage = createAsyncThunk<
  MessageModel,
  { message: string; threadId: number; files: MediaType[] },
  { rejectValue: ErrorType; dispatch: AppDispatch; state: RootState }
>('sendMessage', async ({ message, threadId, files }, { rejectWithValue }) => {
  try {
    const newMessage = {
      body: message,
      documents: files.map(({ id }) => ({
        id,
      })),
    };

    return await api.chat.sendMessage(threadId, newMessage);
  } catch (e) {
    handleServerError(e);
    return rejectWithValue(e as ErrorType);
  }
});

export const getThreadUnreadReplies = createAsyncThunk<
  number[],
  number,
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('getThreadUnreadReplies', async (threadId, { rejectWithValue }) => {
  try {
    return await api.chat.getThreadUnreadReplies(threadId);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const setReaction = createAsyncThunk<
  MessageModel,
  { messageId: number; data: { emoji: string } },
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('setReaction', async ({ messageId, data }, { rejectWithValue }) => {
  try {
    return await api.chat.addReaction(messageId, data);
  } catch (e: any) {
    NotificationService.error(i18next.t('general.notifications.defaultError'));
    return rejectWithValue(e.data as ErrorType);
  }
});

export const removeMessageEmoji = createAsyncThunk<
  MessageModel,
  { messageId: number; emojiKey: string },
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('removeMessageEmoji', async (data, { rejectWithValue }) => {
  try {
    return await api.chat.removeMessageEmoji(data);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const deleteMessage = createAsyncThunk<
  number,
  number,
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('deleteMessage', async (messageId, { rejectWithValue }) => {
  try {
    return await api.chat.deleteMessage(messageId);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const getEmojiUsers =
  (messageId: number, setValue: (data: GetMessageEmojiUsers) => void) => async () => {
    try {
      const res = await api.chat.getUsersReaction(messageId);
      setValue(res);
      return res;
    } catch (e) {
      handleServerError(e);
      return e;
    }
  };

export const editMessage = createAsyncThunk<
  MessageModel,
  { message: string; messageId: number; files: MediaType[] },
  { rejectValue: ErrorType; dispatch: AppDispatch; state: RootState }
>('editMessage', async ({ message, messageId, files }, { rejectWithValue }) => {
  try {
    const newMessage = {
      body: message,
      documents: files.map(({ id }) => ({
        id,
      })),
    };
    return await api.chat.editMessage(newMessage, messageId);
  } catch (e) {
    handleServerError(e);

    return rejectWithValue(e as ErrorType);
  }
});

export const getReplays = createAsyncThunk<
  { message: MessageModel; replays: { data: MessageModel[]; meta: MetaModel } },
  { page: number; replyId: number },
  { rejectValue: ErrorType; dispatch: AppDispatch; state: RootState }
>('getReplays', async ({ replyId, page }, { rejectWithValue }) => {
  try {
    return await api.chat.getReplays(replyId, page);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const sendReplyMessage = createAsyncThunk<
  MessageModel,
  {
    message: string;
    threadId: number;
    files: MediaType[];
    replayId: number;
    createdPendingMessage?: () => void;
  },
  { rejectValue: ErrorType; dispatch: AppDispatch; state: RootState }
>('sendReplyMessage', async ({ message, threadId, files, replayId }, { rejectWithValue }) => {
  try {
    const newMessage = {
      replay_id: replayId,
      body: message,
      documents: files.map(({ id }) => ({
        id,
      })),
    };

    return await api.chat.sendMessage(threadId, newMessage);
  } catch (e) {
    handleServerError(e);
    return rejectWithValue(e as ErrorType);
  }
});

export const getGlobalChatCounters = () => async (dispatch: AppDispatch) => {
  try {
    const res = await api.chat.getGlobalChatCounters();
    dispatch(setChatCounters(res));
    return true;
  } catch (e: any) {
    if (e?.status === StatusServerErrors.AuthorizationException) {
      return e;
    } else {
      handleServerError(e);
      return e;
    }
  }
};

export const createGroupChat = createAsyncThunk<
  ThreadModel,
  {
    data: { subject: string; avatar: { id: number } | null; participants: { id: number }[] };
    setFormLoading: (isLoading: boolean) => void;
  },
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('createGroupChat', async ({ data, setFormLoading }, { rejectWithValue }) => {
  try {
    setFormLoading(true);
    return await api.chat.createGroupChat(data);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  } finally {
    setFormLoading(false);
  }
});

export const getGroupChatUsers = createAsyncThunk<
  ItemUserModel[],
  { threadId: number },
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('getGroupChatUsers', async ({ threadId }, { rejectWithValue, dispatch }) => {
  try {
    dispatch(setLoading(true));
    return await api.chat.getGroupChatUsers(threadId);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  } finally {
    dispatch(setLoading(false));
  }
});

export const inviteToGroupChat = (userId: number, thread_id: number) => async () => {
  try {
    const res = await api.chat.inviteToGroupChat(userId, thread_id);
    NotificationService.success(i18next.t('chat.toasts.addedUserToGroupChat'));
    return res;
  } catch (e) {
    NotificationService.error(i18next.t('general.notifications.defaultError'));
    return e;
  }
};

export const updateGroupThread = createAsyncThunk<
  ThreadModel,
  {
    data: {
      subject: string;
      avatar: { id: number } | null;
      participants: { id: number }[];
    };
    id: number;
    setFormLoading: (isLoading: boolean) => void;

    thread?: ThreadModel | null;
  },
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('updateThread', async ({ data, id, setFormLoading, thread = null }, { rejectWithValue }) => {
  try {
    setFormLoading(true);
    const res = await api.chat.editGroupChat(data, id);
    if (thread) {
      return { ...thread, ...res };
    }
    return res;
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  } finally {
    setFormLoading(false);
  }
});

export const kickFromGroupChat = (userId: number, thread_id: number) => async () => {
  try {
    const res = await api.chat.kickFromGroupChat(userId, thread_id);
    NotificationService.success(i18next.t('general.notifications.userSuccessfullyDeleted'));
    return res;
  } catch (e) {
    handleServerError(e);
    return e;
  }
};

export const leaveGroupChat = (thread_id: number) => async () => {
  try {
    const res = await api.chat.leaveFromGroupChat(thread_id);
    NotificationService.success(i18next.t('chat.toasts.removeMyself'));
    return res;
  } catch (e) {
    handleServerError(e);
    return e;
  }
};

export const deleteGroupChat = (thread_id: number) => async () => {
  try {
    const res = await api.chat.deleteFromGroupChat(thread_id);
    NotificationService.success(i18next.t('chat.toasts.deleted'));
    return res;
  } catch (e) {
    handleServerError(e);
    return e;
  }
};

export const readAllMessages = (thread_id: number) => async () => {
  try {
    const res = await api.chat.readAllThreadMessages(thread_id);
    NotificationService.success(i18next.t('chat.toasts.readAllMessages'));

    return res;
  } catch (e) {
    handleServerError(e);
    return e;
  }
};

export const clearThreadHistory = (thread_id: number) => async () => {
  try {
    const res = await api.chat.clearThreadHistory(thread_id);

    NotificationService.success(i18next.t('chat.toasts.deleteChatHistory'));
    return res;
  } catch (e) {
    handleServerError(e);
    return e;
  }
};

export const getThreadCounter = createAsyncThunk<
  { count_unread_messages: number },
  number,
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('getThreadCounter', async (thread_id, { rejectWithValue, dispatch }) => {
  try {
    const res = await api.chat.getThreadCounter(thread_id);
    dispatch(setThreadCounter({ id: thread_id, count: res.count_unread_messages }));
    return res;
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const fastMessageSend = (message: string, threadId: number) => async (dispatch: AppDispatch) => {
  try {
    const res = await api.chat.sendMessage(threadId, { body: message, documents: [] });
    await dispatch(getThreadCounter(threadId));
    await dispatch(getGlobalChatCounters());
    return res;
  } catch (e) {
    handleServerError(e);
    return e;
  }
};

export const fastReplyMessageSend =
  (message: string, threadId: number, replyId: number) => async (dispatch: AppDispatch) => {
    try {
      const res = await api.chat.sendMessage(threadId, { body: message, documents: [], replay_id: replyId });
      await dispatch(getThreadCounter(threadId));
      await dispatch(getGlobalChatCounters());
      return res;
    } catch (e) {
      handleServerError(e);
      return e;
    }
  };

export const seenThread = (threadId: number) => async () => {
  return await api.chat.seenThread(threadId);
};
export const seenReply = (threadId: number) => async () => {
  return await api.chat.seenReply(threadId);
};

export const handleFindMessage = createAsyncThunk<
  { data: MessageModel[] },
  { id: number; unreadId: number },
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('handleFindMessage', async ({ id, unreadId }, { rejectWithValue }) => {
  try {
    const res1 = await api.chat.getMessages(id, 1, undefined, undefined, unreadId);
    if (res1.data.length) {
      const res2 = await api.chat.getMessages(id, 0, res1.data[res1.data.length - 1].id);
      return { data: [...res1.data, ...res2.data] };
    }

    return res1;
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const handleLoadChatData = createAsyncThunk<
  { thread: ThreadModel; messages: MessageModel[] },
  { id: number },
  { rejectValue: ErrorType; dispatch: AppDispatch; state: RootState }
>('handleLoadChat', async ({ id }, { rejectWithValue, getState }) => {
  try {
    const threads = getState().chat.threads;
    const currentThread = threads?.data?.find((thread: ThreadModel) => thread.id === id);
    if (currentThread?.last_read_message_id) {
      const thread = api.chat.getThread(id);
      const messages = api.chat.getMessages(
        id,
        0,
        undefined,
        undefined,
        undefined,
        currentThread.last_read_message_id,
      );

      return Promise.all([thread, messages]).then((result: any) => {
        return {
          thread: { ...result[0], last_read_message_id: currentThread.last_read_message_id },
          messages: result[1].data as MessageModel[],
        };
      });
    } else {
      const threadPromise = api.chat.getThread(id);

      const { last_read_id } = await api.chat.getThreadLastReedMessageId(id);

      let messages;
      if (last_read_id) {
        messages = await api.chat.getMessages(id, 0, undefined, undefined, undefined, last_read_id);
      } else {
        messages = await api.chat.getMessages(id, 1);
      }
      const thread = await threadPromise;

      return {
        thread: { ...thread, last_read_message_id: last_read_id },
        messages: messages.data as MessageModel[],
      };
    }
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const getThreadLastReedMessageId = createAsyncThunk<
  { last_read_id: number | null },
  number,
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('getThreadLastReedMessageId', async (threadId, { rejectWithValue }) => {
  try {
    return await api.chat.getThreadLastReedMessageId(threadId);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});
